import { useCallback, useLayoutEffect, useMemo, useReducer } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { useEditorRemixCommands } from '@atlassian/malleable-ui-components/editor-remix/commands';
import type { EditorRemixCommandHandlers } from '@atlassian/malleable-ui-components/editor-remix/commands';
import { useEditorRemixLifecycle } from '@atlassian/malleable-ui-components/editor-remix/lifecycle-owner';
import {
	DEFAULT_REMIX_ERROR_MESSAGE,
	REMIX_REMOTE_ABORT_REASON,
} from '@atlassian/malleable-ui-components/types';
import { useIsAnyRemixing } from '@atlassian/malleable-ui-components/state/machine';

import { setIsAnyRemixing } from '../editor-commands/set-is-any-remixing';
import { resetRemixThinkingPresentation } from '../editor-commands/set-remix-thinking-complete';
import { setRemixThinkingLifecycle } from '../editor-commands/set-remix-thinking-lifecycle';
import type { MalleableUiPlugin } from '../malleableUiPluginType';

import { useEditorRemixLifecycleMirror } from './editorRemixLifecycleMirror';
import type { RemixThinkingLifecycleProjection } from './utils/editor-remix-lifecycle-projection';
import {
	consumeEditorRemixMutationInvalidation,
	getEditorRemixLifecyclePresentation,
	getEditorRemixUxHandles,
	subscribeToEditorRemixLifecyclePresentation,
} from './utils/editor-remix-callback-registry';

/** Projects machine lifecycle and terminal effects outside the conditionally mounted Remix UI. */
export const EditorRemixEffectHost = ({
	api,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined;
}): null => {
	const { remixId } = useEditorRemixLifecycle();
	const isAnyRemixing = useIsAnyRemixing();
	const [, refreshPresentation] = useReducer((count: number) => count + 1, 0);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useLayoutEffect(
		() => subscribeToEditorRemixLifecyclePresentation(api, refreshPresentation),
		[api, remixId],
	);
	const presentation = getEditorRemixLifecyclePresentation(api);

	const dispatchProjection = useCallback(
		(projection: RemixThinkingLifecycleProjection) => {
			api?.core.actions.execute(setRemixThinkingLifecycle(projection));
		},
		[api],
	);
	const resetTerminalPresentation = useCallback(
		(commandRemixId: string) => {
			// A stale terminal command must not reset a replacement presentation.
			if (commandRemixId !== remixId) {
				return;
			}
			api?.core.actions.execute(resetRemixThinkingPresentation);
		},
		[api, remixId],
	);

	useEditorRemixLifecycleMirror({
		animationFinished: presentation.animationFinished,
		presentationRemixId: presentation.remixId ?? null,
		dispatchProjection,
	});

	const commandHandlers = useMemo<EditorRemixCommandHandlers>(
		() => ({
			onReady: () => {
				// Lifecycle completion is projected by the mirror; this starts presentation only.
				getEditorRemixUxHandles(api)?.markReady();
			},
			onErrored: (command) => {
				const handles = getEditorRemixUxHandles(api);
				if (!handles) {
					return;
				}
				handles.settleExperience({
					kind: 'failed',
					// Match the legacy fallback when no Rovo store error is available.
					error: new Error(DEFAULT_REMIX_ERROR_MESSAGE),
				});
				// Stop before closing: stop preserves selection but reopens the modal.
				handles.stopThinking({ reopenModal: true });
				resetTerminalPresentation(command.remixId);
				handles.closeTerminalPreview();
				handles.closeModal();
				handles.openSidebar();
			},
			onAborted: (command) => {
				const handles = getEditorRemixUxHandles(api);
				if (!handles) {
					return;
				}
				const presentSourceOrTargetInvalidation = consumeEditorRemixMutationInvalidation(
					api,
					command.remixId,
				);
				// `RemixAborted` is origin-blind, so it settles with the reason legacy reports for an
				// abort this surface did not initiate, keeping the two cohorts comparable. Legacy
				// conflates the same cases under that wording, so matching it reproduces legacy
				// exactly, and the content component still overrides it with a recorded local intent.
				if (
					handles.settleExperience({
						kind: 'aborted',
						reason: REMIX_REMOTE_ABORT_REASON,
						remixId: command.remixId,
					}) === 'ignored'
				) {
					// A different terminal outcome already owns teardown.
					return;
				}
				if (!presentSourceOrTargetInvalidation) {
					handles.stopThinking({ reopenModal: false });
					resetTerminalPresentation(command.remixId);
					return;
				}
				// Source invalidation must preserve the current Rovo Chat sidebar state.
				handles.stopThinking({ reopenModal: true });
				resetTerminalPresentation(command.remixId);
				handles.closeTerminalPreview();
				handles.closeModal();
			},
			onCancellationSettled: () => {
				// Cancellation teardown already handled presentation cleanup.
				getEditorRemixUxHandles(api)?.settleCancellation();
			},
			onHandlerError: ({ commandType, error }) => {
				try {
					createAnalyticsEvent({
						eventType: 'operational',
						action: 'failed',
						actionSubject: 'editorRemixCommandHandler',
						actionSubjectId: commandType,
						source: 'editorRemix',
						attributes: {
							operation: 'commandEffect',
							commandType,
							// Do not log service-supplied error messages.
							errorName: error instanceof Error ? error.name : typeof error,
						},
					}).fire();
				} catch {
					// Diagnostics must never affect command handling.
				}
			},
		}),
		[api, createAnalyticsEvent, resetTerminalPresentation],
	);

	useEditorRemixCommands(commandHandlers);

	useLayoutEffect(() => {
		if (api?.malleableUi?.sharedState?.currentState()?.isAnyRemixing === isAnyRemixing) {
			return;
		}
		api?.core.actions.execute(setIsAnyRemixing(api, isAnyRemixing));
	}, [api, isAnyRemixing]);

	return null;
};
